import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors, ArrowLeft, Plus, X, FloppyDisk, Spinner,
  CheckSquare, ListChecks, MagnifyingGlass, PencilSimple, Trash,
  CheckCircle, ArrowCounterClockwise, Warning, Clock, Calendar,
  Tag as TagIcon, Fire, Flag, User, FileArrowUp, ChartBar,
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { TarefasImportarModal } from './TarefasImportarModal';
import { RelatorioTarefasDrawer } from './RelatorioTarefasDrawer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORIDADES = [
  { id: 'baixa', label: 'Baixa', color: '#71717A', bg: '#F4F4F5' },
  { id: 'normal', label: 'Normal', color: '#1B4332', bg: '#F0FDF4' },
  { id: 'urgente', label: 'Urgente', color: '#B91C1C', bg: '#FEE2E2' },
];

const CATEGORIAS = [
  { id: 'financeiro', label: 'Financeiro', color: '#1B4332', emoji: 'Financeiro' },
  { id: 'cliente', label: 'Cliente', color: '#0369A1' },
  { id: 'equipe', label: 'Equipe', color: '#7C3AED' },
  { id: 'fornecedor', label: 'Fornecedor', color: '#B45309' },
  { id: 'operacao', label: 'Operação', color: '#52B788' },
  { id: 'pessoal', label: 'Pessoal', color: '#A1A1AA' },
];

const STATUSES = {
  pendente: { label: 'Pendente', color: '#B45309', bg: '#FFFBEB' },
  concluida: { label: 'Concluída', color: '#1B4332', bg: '#F0FDF4' },
  atrasada: { label: 'Atrasada', color: '#B91C1C', bg: '#FEE2E2' },
};

const todayBR = () => {
  const now = new Date();
  // Use Intl to format in São Paulo TZ
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' });
  return fmt.format(now); // YYYY-MM-DD
};

const formatDateBR = (ds) => {
  if (!ds) return '';
  const d = new Date(ds + 'T12:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getCatCfg = (id) => CATEGORIAS.find(c => c.id === id) || CATEGORIAS[4];
const getPriCfg = (id) => PRIORIDADES.find(p => p.id === id) || PRIORIDADES[1];

// ==================== MODAL ====================
const TarefaModal = ({ tarefa, onClose, onSuccess, currentUserId }) => {
  const isEdit = !!tarefa;
  const [form, setForm] = useState({
    titulo: tarefa?.titulo || '',
    descricao: tarefa?.descricao || '',
    data: tarefa?.data || todayBR(),
    horario: tarefa?.horario || '',
    prioridade: tarefa?.prioridade || 'normal',
    responsavel: tarefa?.responsavel || '',
    categoria: tarefa?.categoria || 'operacao',
    status: tarefa?.status === 'atrasada' ? 'pendente' : (tarefa?.status || 'pendente'),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.titulo.trim()) { setError('Titulo e obrigatorio'); return; }
    if (!form.data) { setError('Data e obrigatoria'); return; }
    setSaving(true); setError('');
    try {
      const url = isEdit ? `${API}/tarefas/${tarefa.id}` : `${API}/tarefas`;
      const method = isEdit ? 'PUT' : 'POST';
      const body = {
        ...form,
        titulo: form.titulo.trim(),
        descricao: form.descricao.trim(),
        responsavel: form.responsavel.trim(),
        horario: form.horario.trim(),
        created_by_user_id: tarefa?.created_by_user_id || currentUserId || '',
      };
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setError(e?.detail || 'Erro ao salvar');
        return;
      }
      onSuccess();
    } catch { setError('Erro de conexao'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="tarefa-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7] sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
              <CheckSquare size={20} weight="bold" style={{ color: '#1B4332' }} />
            </div>
            <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
              {isEdit ? 'Editar Tarefa' : 'Nova Tarefa'}
            </h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Título *</label>
            <input
              data-testid="input-titulo" type="text" value={form.titulo}
              onChange={(e) => { setForm(f => ({ ...f, titulo: e.target.value })); setError(''); }}
              placeholder="Ex.: Pagar conta de água"
              className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
            />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Descrição</label>
            <textarea
              data-testid="input-descricao" value={form.descricao}
              onChange={(e) => setForm(f => ({ ...f, descricao: e.target.value }))}
              rows={2}
              placeholder="Detalhes opcionais..."
              className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm resize-none focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Data *</label>
              <input
                data-testid="input-data" type="date" value={form.data}
                onChange={(e) => setForm(f => ({ ...f, data: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Horário</label>
              <input
                data-testid="input-horario" type="time" value={form.horario}
                onChange={(e) => setForm(f => ({ ...f, horario: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1.5">Prioridade</label>
            <div className="flex gap-2" data-testid="prioridade-group">
              {PRIORIDADES.map(p => (
                <button
                  key={p.id}
                  data-testid={`btn-pri-${p.id}`}
                  onClick={() => setForm(f => ({ ...f, prioridade: p.id }))}
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium border-2 transition-all`}
                  style={{
                    borderColor: form.prioridade === p.id ? p.color : '#E4E4E7',
                    backgroundColor: form.prioridade === p.id ? p.bg : 'white',
                    color: form.prioridade === p.id ? p.color : '#71717A',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Categoria</label>
              <select
                data-testid="select-categoria" value={form.categoria}
                onChange={(e) => setForm(f => ({ ...f, categoria: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none"
              >
                {CATEGORIAS.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Responsável</label>
              <input
                data-testid="input-responsavel" type="text" value={form.responsavel}
                onChange={(e) => setForm(f => ({ ...f, responsavel: e.target.value }))}
                placeholder="Nome opcional"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none"
              />
            </div>
          </div>

          {isEdit && (
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Status</label>
              <select
                data-testid="select-status" value={form.status}
                onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none"
              >
                <option value="pendente">Pendente</option>
                <option value="concluida">Concluída</option>
              </select>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-1.5 text-red-500 px-3 py-2 bg-red-50 rounded-lg">
              <Warning size={14} weight="fill" /><span className="text-xs font-medium">{error}</span>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A]">
              Cancelar
            </button>
            <button
              data-testid="btn-salvar-tarefa"
              onClick={handleSave}
              disabled={saving || !form.titulo.trim()}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 hover:shadow-lg"
              style={{ backgroundColor: '#1B4332' }}
            >
              {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== TAREFA ROW ====================
const TarefaRow = ({ t, onConcluir, onReabrir, onEdit, onDelete, isToday }) => {
  const stCfg = STATUSES[t.status] || STATUSES.pendente;
  const priCfg = getPriCfg(t.prioridade);
  const catCfg = getCatCfg(t.categoria);
  const isUrgente = t.prioridade === 'urgente';
  const isAtrasada = t.status === 'atrasada';
  const isConcluida = t.status === 'concluida';

  return (
    <div
      data-testid={`tarefa-${t.id}`}
      className={`salonup-card p-4 cursor-default hover:transform-none flex items-start gap-3 group transition-all ${isConcluida ? 'opacity-60' : ''}`}
      style={{
        borderLeft: isUrgente ? '4px solid #B91C1C' : (isAtrasada ? '4px solid #B91C1C' : (isToday ? '4px solid #1B4332' : '')),
      }}
    >
      <button
        data-testid={`btn-toggle-${t.id}`}
        onClick={() => isConcluida ? onReabrir(t.id) : onConcluir(t.id)}
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
        style={{
          backgroundColor: isConcluida ? '#1B4332' : stCfg.bg,
          color: isConcluida ? 'white' : stCfg.color,
        }}
        title={isConcluida ? 'Reabrir' : 'Marcar como concluída'}
      >
        {isConcluida ? <CheckCircle size={18} weight="fill" /> : <CheckSquare size={18} weight="regular" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className={`text-sm font-semibold ${isConcluida ? 'line-through text-[#A1A1AA]' : 'text-[#18181B]'}`} style={{ fontFamily: 'Outfit' }}>
            {t.titulo}
          </h3>
          {isUrgente && !isConcluida && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide" style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}>
              <Fire size={9} weight="fill" />Urgente
            </span>
          )}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide" style={{ backgroundColor: stCfg.bg, color: stCfg.color }}>
            {stCfg.label}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wide" style={{ backgroundColor: `${catCfg.color}15`, color: catCfg.color }}>
            {catCfg.label}
          </span>
        </div>
        {t.descricao && (
          <p className="text-xs text-[#71717A] mb-1 line-clamp-2">{t.descricao}</p>
        )}
        <div className="flex items-center gap-3 text-[11px] text-[#A1A1AA]">
          <span className="inline-flex items-center gap-1">
            <Calendar size={11} weight="bold" />{formatDateBR(t.data)}
            {t.horario && <span className="ml-1 inline-flex items-center gap-0.5"><Clock size={10} weight="bold" />{t.horario}</span>}
          </span>
          {t.responsavel && (
            <span className="inline-flex items-center gap-1">
              <User size={11} weight="bold" />{t.responsavel}
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-1 flex-shrink-0">
        <button
          data-testid={`btn-edit-${t.id}`}
          onClick={() => onEdit(t)}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-[#1B4332] hover:bg-[#F4F4F5] transition-all"
        >
          <PencilSimple size={14} weight="bold" />
        </button>
        <button
          data-testid={`btn-delete-${t.id}`}
          onClick={() => onDelete(t.id, t.titulo)}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
        >
          <Trash size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================
export const TarefasPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterPrioridade, setFilterPrioridade] = useState('todas');
  // V7 refino: por padrao, abrir o modulo com o mes atual selecionado
  const _currentMonthRange = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth(); // 0-11
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0); // day 0 do proximo mes = ultimo dia do mes atual
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { inicio: iso(first), fim: iso(last) };
  })();
  const [filterDataInicio, setFilterDataInicio] = useState(_currentMonthRange.inicio);
  const [filterDataFim, setFilterDataFim] = useState(_currentMonthRange.fim);
  const [search, setSearch] = useState('');
  const [showRelatorio, setShowRelatorio] = useState(false);

  const fetchTarefas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterDataInicio) params.append('data_inicio', filterDataInicio);
      if (filterDataFim) params.append('data_fim', filterDataFim);
      const res = await fetch(`${API}/tarefas?${params.toString()}`);
      setTarefas(await res.json());
    } catch { setTarefas([]); }
    finally { setLoading(false); }
  }, [filterDataInicio, filterDataFim]);

  const periodoInvalido = Boolean(
    filterDataInicio && filterDataFim && filterDataInicio > filterDataFim
  );
  const relatorioHabilitado = Boolean(
    (filterDataInicio || filterDataFim) && !periodoInvalido
  );

  useEffect(() => { fetchTarefas(); }, [fetchTarefas]);

  const handleConcluir = async (id) => {
    await fetch(`${API}/tarefas/${id}/concluir`, { method: 'PUT' });
    fetchTarefas();
  };
  const handleReabrir = async (id) => {
    await fetch(`${API}/tarefas/${id}/reabrir`, { method: 'PUT' });
    fetchTarefas();
  };
  const handleDelete = async (id, titulo) => {
    if (!window.confirm(`Excluir tarefa "${titulo}"?`)) return;
    await fetch(`${API}/tarefas/${id}`, { method: 'DELETE' });
    fetchTarefas();
  };

  const today = todayBR();

  // Apply filters client-side (status/prioridade/search)
  const filteredAll = tarefas.filter(t => {
    if (filterStatus !== 'todos' && t.status !== filterStatus) return false;
    if (filterPrioridade !== 'todas' && t.prioridade !== filterPrioridade) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (t.titulo || '').toLowerCase().includes(q) ||
             (t.descricao || '').toLowerCase().includes(q) ||
             (t.responsavel || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Sort: priority urgente first, then atrasada, then today, then date
  const priWeight = { urgente: 0, normal: 1, baixa: 2 };
  const stWeight = { atrasada: 0, pendente: 1, concluida: 2 };
  const sorted = [...filteredAll].sort((a, b) => {
    if (a.status === 'concluida' && b.status !== 'concluida') return 1;
    if (b.status === 'concluida' && a.status !== 'concluida') return -1;
    const sa = stWeight[a.status] ?? 9;
    const sb = stWeight[b.status] ?? 9;
    if (sa !== sb) return sa - sb;
    const pa = priWeight[a.prioridade] ?? 9;
    const pb = priWeight[b.prioridade] ?? 9;
    if (pa !== pb) return pa - pb;
    return (a.data || '').localeCompare(b.data || '');
  });

  // Split: hoje no topo
  const hoje = sorted.filter(t => t.data === today && t.status !== 'concluida');
  const outras = sorted.filter(t => !(t.data === today && t.status !== 'concluida'));

  // Counters
  const c_total = tarefas.length;
  const c_pendente = tarefas.filter(t => t.status === 'pendente').length;
  const c_atrasada = tarefas.filter(t => t.status === 'atrasada').length;
  const c_concluida = tarefas.filter(t => t.status === 'concluida').length;
  const c_urgente = tarefas.filter(t => t.prioridade === 'urgente' && t.status !== 'concluida').length;

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="tarefas-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark"><Scissors size={22} weight="bold" /></div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button onClick={() => navigate('/gestao')} data-testid="btn-back" className="salonup-btn-back">
            <ArrowLeft size={18} weight="bold" /><span>Voltar para Gestao</span>
          </button>
        </header>

        <div className="mb-4 animate-fade-in">
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/gestao')}>Gestao</span>
            <span className="mx-2">&middot;</span>
            <span className="text-[#18181B] font-medium">Tarefas</span>
          </p>
        </div>

        <section className="mb-6 animate-fade-in-up">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
                <ListChecks size={28} weight="regular" style={{ color: '#1B4332' }} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="salonup-heading-lg">Gestão de Tarefas</h1>
                  <span className="salonup-badge">ROTINA</span>
                </div>
                <p className="salonup-text-sm mt-1">Lembretes, compromissos e pendências da rotina do salão</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="btn-importar-tarefas"
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all hover:shadow-lg border border-[#E4E4E7] bg-white text-[#1B4332]"
              >
                <FileArrowUp size={16} weight="bold" />Importar Tarefas
              </button>
              <button
                data-testid="btn-nova-tarefa"
                onClick={() => { setEditing(null); setShowModal(true); }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg"
                style={{ backgroundColor: '#1B4332' }}
              >
                <Plus size={16} weight="bold" />Nova Tarefa
              </button>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <div className="salonup-card p-3" data-testid="stat-total">
            <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-0.5">Total</p>
            <p className="text-xl font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{c_total}</p>
          </div>
          <div className="salonup-card p-3" data-testid="stat-pendente">
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: '#B45309' }}>Pendentes</p>
            <p className="text-xl font-semibold" style={{ fontFamily: 'Outfit', color: '#B45309' }}>{c_pendente}</p>
          </div>
          <div className="salonup-card p-3" data-testid="stat-atrasada">
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: '#B91C1C' }}>Atrasadas</p>
            <p className="text-xl font-semibold" style={{ fontFamily: 'Outfit', color: '#B91C1C' }}>{c_atrasada}</p>
          </div>
          <div className="salonup-card p-3" data-testid="stat-urgente">
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: '#B91C1C' }}>Urgentes</p>
            <p className="text-xl font-semibold" style={{ fontFamily: 'Outfit', color: '#B91C1C' }}>{c_urgente}</p>
          </div>
          <div className="salonup-card p-3" data-testid="stat-concluida">
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: '#1B4332' }}>Concluídas</p>
            <p className="text-xl font-semibold" style={{ fontFamily: 'Outfit', color: '#1B4332' }}>{c_concluida}</p>
          </div>
        </section>

        {/* Filtros */}
        <section className="mb-5 space-y-3">
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              data-testid="input-search" type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por titulo, descricao ou responsavel..."
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider">Status:</span>
              <div className="flex gap-1 p-1 bg-white border border-[#E4E4E7] rounded-xl">
                {['todos', 'pendente', 'atrasada', 'concluida'].map(s => (
                  <button
                    key={s}
                    data-testid={`filter-status-${s}`}
                    onClick={() => setFilterStatus(s)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-medium capitalize transition-all ${filterStatus === s ? 'bg-[#1B4332] text-white' : 'text-[#71717A] hover:bg-[#F4F4F5]'}`}
                  >
                    {s === 'todos' ? 'Todos' : (STATUSES[s]?.label || s)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider">Prioridade:</span>
              <div className="flex gap-1 p-1 bg-white border border-[#E4E4E7] rounded-xl">
                <button
                  data-testid="filter-pri-todas"
                  onClick={() => setFilterPrioridade('todas')}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${filterPrioridade === 'todas' ? 'bg-[#1B4332] text-white' : 'text-[#71717A] hover:bg-[#F4F4F5]'}`}
                >Todas</button>
                {PRIORIDADES.map(p => (
                  <button
                    key={p.id}
                    data-testid={`filter-pri-${p.id}`}
                    onClick={() => setFilterPrioridade(p.id)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${filterPrioridade === p.id ? 'bg-[#1B4332] text-white' : 'text-[#71717A] hover:bg-[#F4F4F5]'}`}
                  >{p.label}</button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 ml-auto flex-wrap" data-testid="periodo-block">
              <span className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider">Periodo do relatorio:</span>
              <input
                data-testid="filter-data-inicio" type="date" value={filterDataInicio}
                onChange={(e) => setFilterDataInicio(e.target.value)}
                className="px-2.5 py-1 bg-white border border-[#E4E4E7] rounded-lg text-[11px] focus:outline-none focus:ring-1 focus:ring-[#1B4332]/30"
                aria-label="Data inicial"
              />
              <span className="text-[10px] text-[#A1A1AA] px-1">ate</span>
              <input
                data-testid="filter-data-fim" type="date" value={filterDataFim}
                onChange={(e) => setFilterDataFim(e.target.value)}
                className={`px-2.5 py-1 bg-white border rounded-lg text-[11px] focus:outline-none focus:ring-1 focus:ring-[#1B4332]/30 ${periodoInvalido ? 'border-red-400 text-red-700' : 'border-[#E4E4E7]'}`}
                aria-label="Data final"
              />
              {(filterDataInicio !== _currentMonthRange.inicio || filterDataFim !== _currentMonthRange.fim) && (
                <button
                  data-testid="filter-data-clear"
                  onClick={() => {
                    setFilterDataInicio(_currentMonthRange.inicio);
                    setFilterDataFim(_currentMonthRange.fim);
                  }}
                  className="ml-1 text-[11px] text-[#71717A] hover:text-[#1B4332]"
                  title="Restaurar mes atual"
                >Limpar</button>
              )}
              <button
                data-testid="btn-gerar-relatorio"
                onClick={() => setShowRelatorio(true)}
                disabled={!relatorioHabilitado}
                title={
                  periodoInvalido
                    ? 'Data inicial nao pode ser maior que a data final'
                    : (!filterDataInicio && !filterDataFim)
                      ? 'Selecione ao menos uma data'
                      : 'Gerar relatorio do periodo'
                }
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all
                  ${relatorioHabilitado
                    ? 'border border-[#1B4332]/30 bg-[#1B4332]/5 text-[#1B4332] hover:bg-[#1B4332]/10 hover:shadow-sm'
                    : 'border border-[#E4E4E7] bg-white text-[#71717A] opacity-50 cursor-not-allowed'}`}
              >
                <ChartBar size={12} weight="bold" /> Gerar Relatorio
              </button>
              {periodoInvalido && (
                <span className="text-[10px] text-red-600 basis-full sm:basis-auto" data-testid="periodo-invalid-msg">
                  Data inicial nao pode ser maior que a data final.
                </span>
              )}
            </div>
          </div>
        </section>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="salonup-card p-10 text-center max-w-md mx-auto animate-fade-in-up">
            <ListChecks size={36} weight="light" className="mx-auto mb-4 text-[#A1A1AA]" />
            <h3 className="text-lg font-semibold text-[#18181B] mb-2" style={{ fontFamily: 'Outfit' }}>
              {search || filterStatus !== 'todos' || filterPrioridade !== 'todas' || filterDataInicio || filterDataFim ? 'Nenhum resultado' : 'Nenhuma tarefa'}
            </h3>
            <p className="text-sm text-[#71717A]">
              {search || filterStatus !== 'todos' || filterPrioridade !== 'todas' || filterDataInicio || filterDataFim ? 'Ajuste seus filtros.' : 'Clique em "Nova Tarefa" para começar.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4 animate-fade-in-up">
            {hoje.length > 0 && (
              <section data-testid="section-hoje">
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1" style={{ color: '#1B4332' }}>
                  <Flag size={11} weight="fill" />Hoje · {hoje.length}
                </p>
                <div className="space-y-2">
                  {hoje.map(t => (
                    <TarefaRow key={t.id} t={t} isToday onConcluir={handleConcluir} onReabrir={handleReabrir} onEdit={(t) => { setEditing(t); setShowModal(true); }} onDelete={handleDelete} />
                  ))}
                </div>
              </section>
            )}
            {outras.length > 0 && (
              <section data-testid="section-outras">
                {hoje.length > 0 && (
                  <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">Outras tarefas · {outras.length}</p>
                )}
                <div className="space-y-2">
                  {outras.map(t => (
                    <TarefaRow key={t.id} t={t} isToday={false} onConcluir={handleConcluir} onReabrir={handleReabrir} onEdit={(t) => { setEditing(t); setShowModal(true); }} onDelete={handleDelete} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        <footer className="salonup-footer animate-fade-in mt-8">
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salao moderno</p>
        </footer>
      </div>

      {showModal && (
        <TarefaModal
          tarefa={editing}
          currentUserId={user?.id || ''}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSuccess={() => { setShowModal(false); setEditing(null); fetchTarefas(); }}
        />
      )}

      {showImportModal && (
        <TarefasImportarModal
          onClose={() => setShowImportModal(false)}
          onImported={() => fetchTarefas()}
        />
      )}

      {showRelatorio && (
        <RelatorioTarefasDrawer
          dataInicio={filterDataInicio}
          dataFim={filterDataFim}
          filterStatus={filterStatus}
          filterPrioridade={filterPrioridade}
          search={search}
          tarefas={filteredAll}
          onClose={() => setShowRelatorio(false)}
        />
      )}
    </div>
  );
};

export default TarefasPage;
